/**
 * VALUATION-B3 guardrail 1 / VAL-INTAKE-SCOPE-1 §2 — the value-driver intake
 * surface, as a guided three-step wizard.
 *
 * This component replaces `DriverScoreSection`, which rendered one number box
 * per signed driver. The guiding principle it implements, verbatim:
 *
 *   "Drivers are deterministic from the answered questionnaire — the
 *    value-driver score is computed by the engine from the intake answers,
 *    not typed by the valuator."
 *
 * So there is no free-text or numeric input anywhere below. The seller can
 * only pick one of the answer options the ACTIVE config offers, and what goes
 * on the wire is `question_id -> option value`. The score is recomputed
 * server-side by `score_questionnaire()`; the running total shown here is a
 * PREVIEW rendered from the same option points, and it is never sent.
 *
 * VAL-INTAKE-SCOPE-1 R2 reworked this from a flat 25-question accordion into
 * three steps — one per config section, in the fixed order a seller should be
 * walked through (revenue quality, then workforce & operations, then risk /
 * growth / market context) — and split each question's weight into
 * `required` vs corroborating (`DriverQuestion.required`, config data, never
 * a hardcoded question-id list here):
 *
 *   * A required question (marked *) is what `score_questionnaire()` actually
 *     scores; the Next button for a step is gated on every required question
 *     in THAT step being answered, and names the specific question(s) still
 *     missing rather than a generic "complete this section."
 *   * A corroborating question is still asked and still recorded, but never
 *     blocks progression and never moves the preview total (mirrors the
 *     engine's own S2 fence — see driver_questionnaire.py).
 *   * Help text sits behind the ⓘ (`InfoDot`, VAL-PATH P5 / I28) so a required
 *     question's options aren't pushed below the fold by a paragraph nobody
 *     asked for yet.
 *
 * VAL-PATH P5 split one question's block into `_DriverQuestionBlock.tsx`.
 *
 * VAL-INTAKE-SCOPE-1 §3 added the "I'm not sure" control (`_DriverQuestionDefer.tsx`)
 * — separate from the answer options (R3a); `deferred` / `attachments` never enter
 * the answers payload (R3d) but autosave to the draft with `persist` (SAVE-ON-TYPE-2).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import type { DriverQuestion, DriverQuestionnaire } from '@/types/valuation'
import { humanizeDriver } from './_IntakeDrivers'
import { isDocumentAnswerable, type DeferredAttachment } from './_DriverQuestionDefer'
import { QuestionBlock } from './_DriverQuestionBlock'
import { isAnswered } from './_driverAnswers'
import { useDeferredQuestions, type DeferredPersist } from './_useDraftExtras'

const SECTION_LABELS: Record<string, string> = {
  revenue_quality: 'Revenue quality',
  workforce_operations: 'Workforce & operations',
  risk_growth_context: 'Risk, growth & market context',
}

// R2b — the order a seller is walked through. NOT the config's own
// `sections` order (QUESTIONNAIRE_SECTIONS in core/valuation/config_schema.py
// is engine/back-compat order and answers a different question: it does not
// claim to be the right order to ask a person).
const STEP_ORDER = ['revenue_quality', 'workforce_operations', 'risk_growth_context']

function sectionLabel(section: string): string {
  return SECTION_LABELS[section] ?? humanizeDriver(section)
}

export function DriverQuestionnaireSection({
  questionnaire,
  answers,
  onAnswer,
  focusQuestionId,
  persist,
  refusal,
}: {
  questionnaire: DriverQuestionnaire
  answers: Record<string, string>
  onAnswer: (questionId: string, value: string) => void
  /** VAL-POLISH-1 item 5 — from useResumeScroll(), the first unanswered
   * required question at load, or null. Jumps to its step once and focuses
   * it; never re-fires on later edits. */
  focusQuestionId?: string | null
  persist?: DeferredPersist
  /** WALKFIX-A AMEND-2 — the questions a refused run named as unanswered.
   * Each new object marks them red and scrolls to the first; a question
   * stops being red once it is answered again. */
  refusal?: { questionIds: string[] } | null
}) {
  const { user } = useAuth()
  // R3 -- hide the scoring key from the seller (valuation_owner). Every other
  // role (valuation_signer, firm/admin) sees it unchanged, same as today.
  const canSeePoints = user?.role !== 'valuation_owner'

  const bySection = useMemo(() => {
    const map = new Map<string, DriverQuestion[]>()
    for (const section of questionnaire.sections) map.set(section, [])
    for (const q of questionnaire.questions) {
      // A question in a section the config did not declare still has to be
      // askable — dropping it would silently lower the achievable score.
      if (!map.has(q.section)) map.set(q.section, [])
      map.get(q.section)!.push(q)
    }
    return map
  }, [questionnaire])

  // The fixed seller-facing step order, filtered to sections that actually
  // have questions, with any section the config declares that this list
  // doesn't know about appended rather than dropped — same reasoning as
  // `bySection` above.
  const steps = useMemo(() => {
    const known = STEP_ORDER.filter(s => (bySection.get(s)?.length ?? 0) > 0)
    const extra = [...bySection.keys()].filter(
      s => !STEP_ORDER.includes(s) && (bySection.get(s)?.length ?? 0) > 0,
    )
    return [...known, ...extra]
  }, [bySection])

  const [step, setStep] = useState(0)
  // §3 -- `deferred` marks "I'm not sure"; `attachments` the filename attached
  // for a document-answerable question (S15/R3c). Saved to the draft if `persist`.
  const { deferred, setDeferred, attachments, setAttachments } = useDeferredQuestions(persist)

  // R2 (item 5) -- runs once, when focusQuestionId first becomes non-null
  // (Cowork review: driver answers hydrate after the questionnaire, so a
  // useState initializer here ran before the real target was known). Jumps
  // to its step, then scrolls/focuses it once that step is in the DOM.
  // WALKFIX-A AMEND-2 -- a refused run's first named question takes the same
  // jump/scroll/focus, once per refusal.
  const resumeScrolledRef = useRef(false)
  const refusalScrolledRef = useRef<{ questionIds: string[] } | null>(null)
  const [cleared, setCleared] = useState<Set<string>>(() => new Set())
  useEffect(() => { setCleared(new Set()) }, [refusal])
  useEffect(() => {
    const refusalPending = !!refusal && refusal !== refusalScrolledRef.current
    const target = refusalPending
      ? questionnaire.questions.find(q => refusal.questionIds.includes(q.question_id))?.question_id ?? null
      : resumeScrolledRef.current ? null : focusQuestionId ?? null
    if (!target) return
    const idx = steps.findIndex(s => (bySection.get(s) ?? []).some(q => q.question_id === target))
    if (idx !== -1 && idx !== step) {
      setStep(idx)
      return
    }
    resumeScrolledRef.current = true
    if (refusalPending) refusalScrolledRef.current = refusal
    const el = document.getElementById(`question-${target}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.focus()
  }, [focusQuestionId, refusal, questionnaire, step, steps, bySection])

  function handleAnswer(questionId: string, value: string) {
    setCleared(prev => new Set(prev).add(questionId))
    onAnswer(questionId, value)
  }

  // Red: named by the refusal and not re-answered since, or a saved answer
  // that is no longer one of the options (never looks answered).
  function isFailing(question: DriverQuestion): boolean {
    const named = !!refusal?.questionIds.includes(question.question_id) && !cleared.has(question.question_id)
    const stale = (answers[question.question_id] ?? '') !== '' && !isAnswered(question, answers)
    return named || stale
  }

  function handleDefer(questionId: string) {
    setDeferred(prev => ({ ...prev, [questionId]: true }))
    const question = questionnaire.questions.find(q => q.question_id === questionId)
    if (question && isDocumentAnswerable(question)) {
      // R3e -- deferring clears any prior hand-typed answer; a deferred
      // document-answerable question is unresolved, not silently pre-filled.
      onAnswer(questionId, '')
      setAttachments(prev => {
        const next = { ...prev }
        delete next[questionId]
        return next
      })
    }
  }

  function handleCancelDefer(questionId: string) {
    setDeferred(prev => ({ ...prev, [questionId]: false }))
  }

  function handleAttach(questionId: string, file: DeferredAttachment) {
    setAttachments(prev => ({ ...prev, [questionId]: file }))
  }

  // S17 -- a deferred, document-answerable question with nothing attached
  // yet is blocked for a different reason than a plain unanswered question:
  // it's waiting on a document, not a typed answer (R3d: same engine rule,
  // different reason shown to the seller).
  function isDeferredAwaitingDocument(question: DriverQuestion): boolean {
    return Boolean(deferred[question.question_id]) && isDocumentAnswerable(question) && !attachments[question.question_id]
  }

  function describeMissing(question: DriverQuestion): string {
    return isDeferredAwaitingDocument(question)
      ? `${question.prompt} (document required)`
      : question.prompt
  }

  const clampedStep = Math.min(step, Math.max(0, steps.length - 1))
  const currentSection = steps[clampedStep]
  const currentQuestions = currentSection ? bySection.get(currentSection) ?? [] : []

  const requiredHere = currentQuestions.filter(q => q.required)
  const missingRequiredHere = requiredHere.filter(q => !isAnswered(q, answers))
  const answeredRequiredHere = requiredHere.length - missingRequiredHere.length
  const canAdvance = missingRequiredHere.length === 0
  const isLastStep = clampedStep === steps.length - 1

  const allRequired = questionnaire.questions.filter(q => q.required)
  const missingRequiredOverall = allRequired.filter(q => !isAnswered(q, answers))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Value drivers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* R2a — disclosure, step 1 only. No completion-time estimate; the
            required count is read off the config, not hardcoded, so this
            stays true whether v1 (all required) or v2 (11 of 25) is active.
            R2f — the "rest corroborate" sentence only makes sense when a
            "rest" exists; under an all-required config (v1) it is dropped
            rather than claiming a corroborating set of zero. */}
        {clampedStep === 0 && (
          <div className="space-y-1 rounded-lg border border-pal-line bg-pal-surface-subtle p-3 text-xs text-pal-ink-soft">
            <p>
              {questionnaire.questions.length} questions. {allRequired.length} are required —
              marked *.
              {allRequired.length < questionnaire.questions.length &&
                ' The rest corroborate what you already told us; answering them helps but they never move the score.'}
            </p>
            <p>Score conservatively: when a reading is between two options, the lower one wins.</p>
            <p>Don't guess — mark "I'm not sure" and we'll say which document settles it.</p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-medium text-gray-900">
            Step {clampedStep + 1} of {steps.length}: {sectionLabel(currentSection ?? '')}
          </span>
          {/* R2b — progress reads "N of M required" for THIS step, never a
              share of all 25/all-questions. */}
          <span className="text-gray-500">
            {answeredRequiredHere} of {requiredHere.length} required
          </span>
        </div>

        <div className="space-y-3">
          {currentQuestions.map(q => (
            <QuestionBlock
              key={q.question_id}
              question={q}
              answer={answers[q.question_id]}
              onAnswer={handleAnswer}
              deferred={deferred[q.question_id] ?? false}
              attachment={attachments[q.question_id]}
              onDefer={handleDefer}
              onCancelDefer={handleCancelDefer}
              onAttach={handleAttach}
              canSeePoints={canSeePoints}
              failing={isFailing(q)}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3">
          <Button
            type="button"
            variant="outline"
            disabled={clampedStep === 0}
            onClick={() => setStep(s => Math.max(0, s - 1))}
          >
            Back
          </Button>

          {!isLastStep ? (
            <div className="flex flex-col items-end gap-1">
              {/* R2c — disabled until every required question in THIS step is
                  answered; corroborating questions never block. */}
              <Button
                type="button"
                disabled={!canAdvance}
                onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))}
              >
                Next
              </Button>
              {!canAdvance && (
                <p className="max-w-sm text-right text-xs text-red-700">
                  Answer to continue: {missingRequiredHere.map(q => describeMissing(q)).join('; ')}
                </p>
              )}
            </div>
          ) : missingRequiredOverall.length > 0 ? (
            <span className="text-xs text-red-700">
              {missingRequiredOverall.length} required question
              {missingRequiredOverall.length === 1 ? '' : 's'} still unanswered — the run is
              blocked until every required question has an answer
              {missingRequiredOverall.some(q => isDeferredAwaitingDocument(q)) &&
                ' (some are waiting on a document, not a typed answer)'}
            </span>
          ) : (
            // R11 — no score number here for any role; scoring happens
            // server-side and is never previewed on this screen.
            <span className="text-sm font-semibold text-gray-900">
              All required questions answered
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
